// CollectionHeader.jsx - Collection header with selection count, share, and add game
import React, { useState } from "react";
import { Search, Share2, RefreshCw, Plus, Info } from "lucide-react";
import './ModalComponents.css';

/**
 * Collection header component with editable name, share button,
 * curate button, and add game dropdown - unified row layout
 */
function CollectionHeader({
  filteredCollectionCount,
  // Actions (from both components)
  onShareModalOpen,
  onToggleEditControls,
  onAddGame,
  // Filter mode for ownership switch
  filterMode,
  onFilterModeChange,
  // Pending changes and refresh
  pendingChangesCount,
  onRefreshCollection,
  // Shared collection awareness
  isSharedCollection = false,
}) {
  const [showNotForMeInfo, setShowNotForMeInfo] = useState(false);

  return (
    <div className="collection-header">
      {/* Unified Row: Name Section (left) + Action Buttons (right) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "clamp(0.5rem, 2vw, 2rem)",
          marginBottom: "1rem",
          flexWrap: "nowrap",
          padding: "0.5rem 0",
          backgroundColor: "var(--bg-secondary, #1e1e1e)",
          margin: "0 0.5rem 0",
        }}
      >
        {/* Left Section: Collection Name and Share/Refresh */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "clamp(0.5rem, 1vw, 0.75rem)",
            flex: "0 0 auto",
            minWidth: "fit-content",
            backgroundColor: "var(--bg-secondary, #1e1e1e)",
            padding: "0.5rem 0.75rem",
          }}
        >
          <h3
            style={{
              padding: "0.5rem 0.75rem",
              borderRadius: "4px",
              display: "inline-block",
              margin: 0,
              fontSize: "clamp(1rem, 3vw, 1.25rem)",
              whiteSpace: "nowrap",
              flexShrink: 1,
              minWidth: "0",
            }}
          >
            {filteredCollectionCount} found
          </h3>

          {pendingChangesCount > 0 && (
            <button
              onClick={onRefreshCollection}
              className="profile-button refresh-collection-button"
              aria-label="Refresh collection"
              title={`Refresh to apply ${pendingChangesCount} pending change${pendingChangesCount > 1 ? 's' : ''}`}
              style={{
                backgroundColor: "var(--pista-green-primary, #2E7D32)",
                color: "var(--pista-button-text, white)",
                border: "2px solid var(--pista-green-primary, #2E7D32)",
                borderRadius: "8px",
                padding: "0.375rem 0.5rem",
                fontSize: "0.8rem",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: "0 2px 4px rgba(46, 125, 50, 0.3)",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.375rem",
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = "var(--primary-color-dark, #1565c0)";
                e.target.style.transform = "scale(1.05)";
                e.target.style.boxShadow = "0 4px 10px rgba(25, 118, 210, 0.4)";
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = "var(--primary-color, #1976d2)";
                e.target.style.transform = "scale(1)";
                e.target.style.boxShadow = "0 2px 6px rgba(25, 118, 210, 0.3)";
              }}
            >
              <RefreshCw size={14} /> ({pendingChangesCount})
            </button>
          )}
        </div>

        {/* Right Section: Action Buttons */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "clamp(0.5rem, 1.5vw, 0.75rem)",
            flexWrap: "nowrap",
            flex: "0 0 auto",
            backgroundColor: "var(--bg-secondary, #1e1e1e)",
            padding: "0.5rem 0.75rem",
            margin: "0 0.5rem 0",
          }}
        >
          {/* Mobile Edit Controls Toggle */}
          {/* Share Collection button */}
              {!isSharedCollection && (
                <button
                  onClick={onShareModalOpen}
                  className="profile-button share-collection-button"
                  aria-label="Share Library"
                  style={{
                    backgroundColor: "var(--pista-green-primary, #2E7D32)",
                    color: "var(--pista-button-text, white)",
                    border: "2px solid var(--pista-green-primary, #2E7D32)",
                    borderRadius: "8px",
                    padding: "clamp(0.375rem, 1.5vw, 0.5rem) clamp(0.5rem, 2vw, 0.75rem)",
                    fontSize: "clamp(0.8rem, 2.5vw, 0.9rem)",
                    fontWeight: "600",
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(46, 125, 50, 0.3)",
                    transition: "all 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.375rem",
                    minHeight: "clamp(40px, 10vw, 50px)",
                    minWidth: "clamp(40px, 10vw, 50px)",
                    flexShrink: 1,
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = "var(--pista-green-hover, #388E3C)";
                    e.target.style.transform = "scale(1.02)";
                    e.target.style.boxShadow = "0 4px 12px rgba(46, 125, 50, 0.4)";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = "var(--pista-green-primary, #2E7D32)";
                    e.target.style.transform = "scale(1)";
                    e.target.style.boxShadow = "0 2px 4px rgba(46, 125, 50, 0.3)";
                  }}
                >
                  <Share2 size={16} />
                </button>
              )}
          <button
            onClick={onToggleEditControls}
            className="icon-button edit-controls-toggle"
            aria-label="Toggle collection filters and search"
            title="Toggle search and filter options for your collection"
            style={{
              backgroundColor: "var(--pista-green-primary, #2E7D32)",
              color: "var(--pista-button-text, white)",
              border: "2px solid var(--pista-green-primary, #2E7D32)",
              borderRadius: "8px",
              padding: "clamp(0.375rem, 1.5vw, 0.5rem) clamp(0.5rem, 2vw, 0.75rem)",
              fontSize: "clamp(0.8rem, 2.5vw, 0.9rem)",
              fontWeight: "600",
              cursor: "pointer",
              boxShadow: "0 2px 4px rgba(46, 125, 50, 0.3)",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "clamp(40px, 10vw, 50px)",
              minWidth: "clamp(40px, 10vw, 50px)",
              flexShrink: 1,
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = "var(--pista-green-primary-dark, #1B5E20)";
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 6px 16px rgba(46, 125, 50, 0.4)";
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = "var(--pista-green-primary, #2E7D32)";
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "0 4px 12px rgba(46, 125, 50, 0.3)";
            }}
          >
            <Search size={16} />
          </button>

          {/* Add Games Button - Green and Prominent */}
          {!isSharedCollection && (
            <button
              onClick={onAddGame}
              className="profile-button add-game-button"
              aria-label="Add a game to library"
            style={{
              backgroundColor: "var(--pista-green-primary, #2E7D32)",
              color: "var(--pista-button-text, white)",
              border: "2px solid var(--pista-green-primary, #2E7D32)",
              borderRadius: "8px",
              padding: "clamp(0.375rem, 1.5vw, 0.5rem) clamp(0.5rem, 2vw, 0.75rem)",
              fontSize: "clamp(0.8rem, 2.5vw, 0.9rem)",
              fontWeight: "600",
              cursor: "pointer",
              boxShadow: "0 2px 4px rgba(46, 125, 50, 0.3)",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              minHeight: "clamp(40px, 10vw, 50px)",
              minWidth: "clamp(40px, 10vw, 50px)",
              flexShrink: 1,
              overflow: "hidden",
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = "var(--pista-green-hover, #388E3C)";
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 6px 16px rgba(46, 125, 50, 0.4)";
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = "var(--pista-green-primary, #2E7D32)";
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "0 4px 12px rgba(46, 125, 50, 0.3)";
            }}
          >
            <Plus size={18} />
          </button>
          )}
        </div>
      </div>

      {!isSharedCollection && (
        <div
          className="ownership-switch"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.75rem",
            marginBottom: "1rem",
            backgroundColor: "var(--bg-secondary, #1e1e1e)"
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              border: "2px solid var(--border-color, #e0e0e0)",
              borderRadius: "20px",
              backgroundColor: "var(--bg-secondary, #1e1e1e)",
              padding: "2px",
            }}
          >
            <button
              onClick={() => onFilterModeChange("all")}
              style={{
                padding: "0.5rem 0.75rem",
                border: "none",
                borderRadius: "18px",
                backgroundColor:
                  filterMode === "all"
                    ? "var(--pista-button-bg, #1976d2)"
                    : "var(--bg-secondary, #1e1e1e)",
                color:
                  filterMode === "all"
                    ? "var(--pista-button-text, white)"
                    : "var(--text-primary, #333)",
                fontSize: "0.9rem",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              All
            </button>
            <div
              style={{
                width: "2px",
                height: "16px",
                backgroundColor: "var(--border-color, #ddd)",
              }}
            />
            <button
              onClick={() => onFilterModeChange("owned")}
              style={{
                padding: "0.5rem 0.75rem",
                border: "none",
                borderRadius: "18px",
                backgroundColor:
                  filterMode === "owned"
                    ? "var(--pista-button-bg, #1976d2)"
                    : "var(--bg-secondary, #1e1e1e)",
                color:
                  filterMode === "owned"
                    ? "var(--pista-button-text, white)"
                    : "var(--text-primary, #333)",
                fontSize: "0.9rem",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              I have this
            </button>
            <div
              style={{
                width: "2px",
                height: "16px",
                backgroundColor: "var(--border-color, #ddd)",
              }}
            />
            <button
              onClick={() => onFilterModeChange("interested")}
              style={{
                padding: "0.5rem 0.75rem",
                border: "none",
                borderRadius: "18px",
                backgroundColor:
                  filterMode === "interested"
                    ? "var(--pista-button-bg, #1976d2)"
                    : "var(--bg-secondary, #1e1e1e)",
                color:
                  filterMode === "interested"
                    ? "var(--pista-button-text, white)"
                    : "var(--text-primary, #333)",
                fontSize: "0.9rem",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              Interested
            </button>
            <div
              style={{
                width: "2px",
                height: "16px",
                backgroundColor: "var(--border-color, #ddd)",
              }}
            />
            <button
              onClick={() => onFilterModeChange("excluded")}
              style={{
                padding: "0.5rem 0.75rem",
                border: "none",
                borderRadius: "18px",
                backgroundColor:
                  filterMode === "excluded"
                    ? "var(--pista-button-bg, #1976d2)"
                    : "var(--bg-secondary, #1e1e1e)",
                color:
                  filterMode === "excluded"
                    ? "var(--pista-button-text, white)"
                    : "var(--text-primary, #333)",
                fontSize: "0.9rem",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              Not for me
            </button>
            <button
              type="button"
              onClick={() => setShowNotForMeInfo(!showNotForMeInfo)}
              aria-label="Not for me info"
              title="What does Not for me mean?"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0.25rem",
                marginLeft: "0.25rem",
                background: "transparent",
                border: "none",
                color: "var(--text-secondary, #666)",
                cursor: "pointer",
                borderRadius: "50%"
              }}
            >
              <Info size={14} />
            </button>
          </div>
          {showNotForMeInfo && (
            <div
              style={{
                marginTop: "0.5rem",
                padding: "0.5rem 0.75rem",
                backgroundColor: "var(--bg-tertiary, #3d3d3d)",
                border: "1px solid var(--border-color, #404040)",
                borderRadius: "8px",
                fontSize: "0.85rem",
                color: "var(--text-primary, #e0e0e0)",
                maxWidth: "320px",
                textAlign: "center"
              }}
            >
              Games you&apos;ve ruled out. They stay here so they don&apos;t slip back into your recommendations, but you can change your mind any time.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CollectionHeader;
